import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useImpersonation } from '../contexts/ImpersonationContext.jsx';

function syncNetworkEmail(navigate, location, email) {
  if (!location.pathname.startsWith('/my-network')) return;
  const params = new URLSearchParams(location.search);
  if (email) params.set('email', email);
  else params.delete('email');
  const search = params.toString();
  navigate(
    { pathname: '/my-network', search: search ? `?${search}` : '' },
    { replace: true }
  );
}

export default function AdminViewAsControl() {
  const { isAdmin, impersonatedUser, setImpersonatedUser } = useImpersonation();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const search = query.trim();
    if (search.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch('/api/search-users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ search }),
        });
        const data = response.ok ? await response.json() : { records: [] };
        if (!cancelled) setResults(data.records || []);
      } catch (error) {
        console.error('[AdminViewAs] user search failed:', error);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  if (!isAdmin) return null;

  const selectUser = (record, close) => {
    setImpersonatedUser(record);
    syncNetworkEmail(navigate, location, record.email);
    setQuery('');
    setResults([]);
    close();
  };

  const clearUser = () => {
    setImpersonatedUser(null);
    syncNetworkEmail(navigate, location, '');
    setQuery('');
    setResults([]);
  };

  const activeLabel = impersonatedUser?.fullName || impersonatedUser?.email;

  return (
    <div className="fixed right-4 top-24 z-[80] flex items-center gap-2">
      {impersonatedUser?.email ? (
        <div className="flex max-w-[220px] items-center gap-1 rounded-lg border border-gray-200 bg-white py-1 pl-2.5 pr-1 shadow-sm">
          <span className="truncate text-xs font-medium text-gray-900">{activeLabel}</span>
          <button
            type="button"
            onClick={clearUser}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            aria-label="Stop viewing as this user"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}

      <Popover className="relative">
        <PopoverButton
          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 ${
            impersonatedUser?.email
              ? 'border-gray-900 bg-gray-900 text-white hover:bg-gray-800'
              : 'border-gray-200 bg-white text-gray-500 hover:text-gray-900'
          }`}
          aria-label="View page as a user"
        >
          <UserCircleIcon className="h-5 w-5" />
        </PopoverButton>

        <PopoverPanel className="absolute right-0 top-full z-[80] mt-2 w-80 rounded-xl border border-gray-100 bg-white p-4 shadow-xl">
          {({ close }) => (
            <div>
              <p className="text-sm font-semibold text-gray-900">View as user</p>
              <p className="mt-1 text-sm text-gray-500">
                Search the users table. On the grant form, stage tabs stay available.
              </p>
              <label className="mt-4 block text-xs font-medium text-gray-700 mb-1" htmlFor="admin-view-as-search">
                User
              </label>
              <input
                id="admin-view-as-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by email"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors"
                autoFocus
              />
              <div className="mt-2 max-h-64 overflow-y-auto">
                {loading ? (
                  <p className="px-1 py-2 text-sm text-gray-500">Searching…</p>
                ) : query.trim().length < 2 ? (
                  <p className="px-1 py-2 text-sm text-gray-500">Type at least 2 characters.</p>
                ) : results.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-gray-500">No users found.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {results.map((record) => (
                      <li key={record.value || record.email}>
                        <button
                          type="button"
                          onClick={() => selectUser(record, close)}
                          className="w-full rounded-lg px-2 py-2 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="block truncate text-sm font-medium text-gray-900">
                            {record.fullName || record.email}
                          </span>
                          {record.fullName ? (
                            <span className="block truncate text-xs text-gray-500">{record.email}</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {impersonatedUser?.email ? (
                <button
                  type="button"
                  onClick={() => {
                    clearUser();
                    close();
                  }}
                  className="mt-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Stop viewing as {impersonatedUser.email}
                </button>
              ) : null}
            </div>
          )}
        </PopoverPanel>
      </Popover>
    </div>
  );
}
